import { getDaysInterval, getNextMonday, isSameDay } from "@components/inputs/date/utils";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import {
    IPrPayrollSettingEntity,
    IPrWorkingPatternDayEntity,
    IPrWorkingPatternDayIntervalEntity,
    IPrWorkingPatternEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import { PrWorkingPatternTypeCode } from "@odata/GeneratedEnums";
import { getEmploymentGeneralSettings } from "@pages/payroll/employment/Employment.utils";
import { getTimeSpanFromHours } from "@pages/payroll/workingPatterns/WorkDayIntervals.utils";
import {
    createDefaultIntervalsForDay,
    deleteIntervalErrors,
    getWeeklyWorkingHours,
} from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import Big from "big.js";
import { Dayjs } from "dayjs";
import { cloneDeep } from "lodash";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { AwaitedArray } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { getUtcDate, getUtcDayjs } from "../../../types/Date";
import { IContextInitArgs, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import {
    getHoursFromTimeStamp,
    pairIntervalsWithExistingIds,
    transformWorkIntervalsBeforeSave
} from "../attendance/Attendance.utils";
import {
    dateStartPath,
    rotationLengthPath,
    SHIFT_GROUP_ID,
    workingPatternFullTimeValuePath
} from "./WorkingPatternsDef";


export interface IWorkingPatternCustomData extends IFormStorageDefaultCustomData {
    selectedDay: BindingContext;
}

class WorkingPatternsFormView extends FormViewForExtend<IPrWorkingPatternEntity, IFormViewProps<IPrWorkingPatternEntity, IWorkingPatternCustomData>> {
    static contextType = AppContext;
    _selectedDayIdx: number = null;

    static defaultProps = {
        title: "WorkingPatterns:FormTitle",
        formProps: {
            shouldHideVariant: true
        }
    };

    getAdditionalLoadPromise(args: IContextInitArgs) {
        return [
            getEmploymentGeneralSettings(this.props.storage.oData),
            ...super.getAdditionalLoadPromise(args)
        ];
    }

    getAdditionalResults(): AwaitedArray<ReturnType<WorkingPatternsFormView["getAdditionalLoadPromise"]>> {
        return this.props.storage.data.additionalResults;
    }

    onAfterLoad() {
        const { storage } = this.props;
        const isNew = storage.data.bindingContext.isNew();
        if (!isNew && this.entity.TypeCode !== PrWorkingPatternTypeCode.Uneven) {
            const sortedDays = this.entity.Days?.sort((day, day2) => getUtcDayjs(day.Date).diff(day2.Date));
            const date = sortedDays[0].Date;
            storage.setValueByPath(dateStartPath, date ?? getUtcDate());
            if (this.entity.TypeCode === PrWorkingPatternTypeCode.Rotation) {
                storage.setValueByPath(rotationLengthPath, this.entity.Days?.length ?? 0);
            }
        }

        if (isNew) {
            this.initDefaultWorkingHoursPerWeek();
        }

        if (this._selectedDayIdx) {
            // in case working pattern is new, selected BC after save changes, so we need to update it with correct day BC
            const selectedDay = this.entity.Days?.[this._selectedDayIdx];
            if (selectedDay) {
                storage.setCustomData({ selectedDay: storage.data.bindingContext.navigate(PrWorkingPatternEntity.Days).addKey(selectedDay) });
            }
        }

        if (!this.entity.Days) {
            this.entity.Days = [];
        }

        return super.onAfterLoad();
    }

    initDefaultWorkingHoursPerWeek() {
        const employmentGeneralSettings = this.getAdditionalResults()[0] as IPrPayrollSettingEntity;
        this.entity.HoursPerWeekFullTime = employmentGeneralSettings?.CurrentTemporalPropertyBag?.WorkingHoursPerWeek ?? 40;
    }

    handleTypeChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrWorkingPatternEntity.TypeCode) {
            if (this.entity.TypeCode !== e.value) {
                const storage = this.props.storage;

                let _rotationLength = 0;
                const isNew = storage.data.bindingContext.isNew();
                const origEntity = storage.data.origEntity;
                const isSwitchingBackToSaved = !isNew && e.value === origEntity.TypeCode;
                this.entity.Days = isSwitchingBackToSaved ? origEntity.Days : [];
                if (isSwitchingBackToSaved) {
                    if (e.value === PrWorkingPatternTypeCode.Rotation) {
                        _rotationLength = origEntity.Days?.length ?? 0;
                        storage.setValueByPath(rotationLengthPath, _rotationLength);
                    }
                } else {
                    // clear rotation length when type changes as it is not relevant for new type, and has validation
                    storage.clearValueByPath(rotationLengthPath);
                }

                const workingMinutesWeekly = getWeeklyWorkingHours({
                    ...this.entity,
                    TypeCode: e.value as PrWorkingPatternTypeCode
                }, _rotationLength) * 60;
                storage.setValueByPath(PrWorkingPatternEntity.WorkingMinutesWeekly, workingMinutesWeekly > 0 ? Math.ceil(workingMinutesWeekly) : null);

                storage.setCustomData({
                    selectedDay: null
                });
                storage.refresh();
            }
        }
    };

    handleDateStartChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === dateStartPath) {
            const storage = this.props.storage;
            const newStartDate: Dayjs = getUtcDayjs(e.value as Date);
            if (!isSameDay(newStartDate, getUtcDayjs(storage.getValueByPath(dateStartPath)))) {
                const newDays: IPrWorkingPatternDayEntity[] = [];
                if (this.entity.TypeCode === PrWorkingPatternTypeCode.Rotation && !storage.validateFieldSync(e.bindingContext)) {
                    const newLength = storage.getValueByPath(rotationLengthPath);
                    const originalDays = cloneDeep(this.entity.Days);
                    for (let i = 0; i < newLength && i < originalDays?.length; i++) {
                        newDays.push({
                            ...originalDays[i],
                            Date: newStartDate.add(i, "day").toDate()
                        });
                    }
                }
                storage.setValueByPath(PrWorkingPatternEntity.Days, newDays);
                storage.setCustomData({
                    selectedDay: null
                });
                storage.refresh();
            }
        }
    };


    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        await super.handleBlur(args);

        if (args.bindingContext.getPath(true) === PrWorkingPatternEntity.HoursPerWeekFullTime) {
            const { storage } = this.props;
            storage.addActiveField(storage.data.bindingContext.navigate(workingPatternFullTimeValuePath));
            storage.refreshFields(true);
        }
    }

    handleWeeklyWorkingHoursChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === PrWorkingPatternEntity.WorkingMinutesWeekly && this.entity.TypeCode === PrWorkingPatternTypeCode.Weekly) {
            const storage = this.props.storage;
            if (!isNaN(Number(e.value))) {
                const daysToShareTheLoad = 5; // monday to friday
                // value is in minutes, we need to convert it to hours and divide by number of days to share the load
                const hoursVal = new Big(Number(e.value)).div(60);
                const hours = hoursVal.div(daysToShareTheLoad).round(1);
                const remainingHours = hoursVal.minus(hours.times(daysToShareTheLoad));

                const dayIntervals = createDefaultIntervalsForDay(storage, hours.toNumber());
                let firstMondayIntervals: IPrWorkingPatternDayIntervalEntity[];
                if (remainingHours.toNumber() > 0) {
                    firstMondayIntervals = createDefaultIntervalsForDay(storage, hours.plus(remainingHours).toNumber());
                }

                const startDate = getUtcDayjs(storage.getValueByPath(dateStartPath));
                const intervalLength = this.entity.IsDifferentOddAndEvenWeek ? 14 : 7;
                const days = getDaysInterval(getNextMonday(startDate, this.entity.IsDifferentOddAndEvenWeek), intervalLength)
                    .filter(d => d.day() > 0 && d.day() < 6) // weekdays only
                    .map((d, index) => {
                        const isMonday = d.day() === 1;
                        return BindingContext.createNewEntity<IPrWorkingPatternDayEntity>(index, {
                            Date: d.toDate(),
                            Intervals: isMonday && !!firstMondayIntervals ? firstMondayIntervals : dayIntervals,
                            WorkingHours: isMonday && !!firstMondayIntervals ? getTimeSpanFromHours(hours.plus(remainingHours).toNumber()) : getTimeSpanFromHours(hours.toNumber())
                        });
                    });

                // pair with existing days to keep their IDs, so that we don't create new days every time the weekly working hours changes
                for (const origDay of storage.data.origEntity?.Days ?? []) {
                    const day = days.find(d => isSameDay(getUtcDayjs(d.Date), getUtcDayjs(origDay.Date)));
                    if (day) {
                        day.Id = origDay.Id;
                        delete day[BindingContext.NEW_ENTITY_ID_PROP];
                    }
                }

                storage.setValueByPath(PrWorkingPatternEntity.Days, days);
                storage.refreshGroupByKey(SHIFT_GROUP_ID);
            }
        }
    };

    handleChange = (e: ISmartFieldChange): void => {
        const { storage } = this.props;
        this.handleTypeChange(e);
        this.handleDateStartChange(e);
        this.handleWeeklyWorkingHoursChange(e);

        if (e.bindingContext.getPath() === PrWorkingPatternEntity.IsDifferentOddAndEvenWeek) {
            storage.setValueByPath(PrWorkingPatternEntity.Days, []);
            storage.setValueByPath(PrWorkingPatternEntity.WorkingMinutesWeekly, 0);
            storage.refresh(true);
        }

        storage.handleChange(e);

        if ([dateStartPath, PrWorkingPatternEntity.IsDifferentOddAndEvenWeek, rotationLengthPath].includes(e.bindingContext.getPath())) {
            storage.validateFieldSync(e.bindingContext);
            if (!storage.hasError(e.bindingContext)) {
                storage.refresh();
                return;
            }
        }
        storage.refreshFields(e.triggerAdditionalTasks);
    };

    fillWithDefaultZeros = (data: IPrWorkingPatternEntity): IPrWorkingPatternEntity => {
        const sortedDays = data.Days.sort((day1, day2) => {
            return getUtcDayjs(day1.Date).diff(day2.Date);
        });

        function findDay(d: IPrWorkingPatternDayEntity) {
            return isSameDay(getUtcDayjs(d.Date), date);
        }

        let date: Dayjs;
        let length: number;
        switch (data.TypeCode) {
            case PrWorkingPatternTypeCode.Weekly:
                date = getUtcDayjs(sortedDays[0]?.Date).clone().startOf("week");
                length = data.IsDifferentOddAndEvenWeek ? 14 : 7;
                break;
            case PrWorkingPatternTypeCode.Rotation:
                date = getUtcDayjs(this.props.storage.getValueByPath(dateStartPath));
                length = this.props.storage.getValueByPath(rotationLengthPath);
                break;
        }

        const days: IPrWorkingPatternDayEntity[] = [];

        for (let i = 0; i < length; i++) {
            const day = sortedDays.find(findDay);

            days.push(day ?? {
                Date: date.toDate(),
                WorkingHours: getTimeSpanFromHours(0)
            });
            date = date.add(1, "day");
        }

        data.Days = days;
        return data;
    };

    onBeforeSave(): IPrWorkingPatternEntity {
        let data = this.entity;

        for (const day of (data.Days ?? [])) {
            if (!day.WorkingHours || getHoursFromTimeStamp(day.WorkingHours) === 0) {
                deleteIntervalErrors(this.props.storage, day);

                day.WorkingHours = getTimeSpanFromHours(0);
                day.Intervals = [];
            } else {
                day.Intervals = transformWorkIntervalsBeforeSave(day.Intervals);
            }
        }

        data = pairIntervalsWithExistingIds(this.props.storage, data);

        if (data.TypeCode !== PrWorkingPatternTypeCode.Uneven) {
            data = this.fillWithDefaultZeros(data);

            // Uneven working pattern has field validation on WorkingMinutesWeekly, other types needs special
            // error message as the field is not visible.
            const weeklyHours = data.WorkingMinutesWeekly / 60;
            if (weeklyHours > data.HoursPerWeekFullTime) {
                throw new Error(this.props.storage.t("WorkingPatterns:ErrorWeeklyHoursExceedFullTime"));
            }
        }

        return data;
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean): void {
        const { storage } = this.props;
        const selected = storage.getCustomData().selectedDay;
        if (isNew && selected) {
            const key = selected.getKey();
            const noKeyBc = selected.removeKey();
            this._selectedDayIdx = storage.data.entity.Days?.findIndex(d => noKeyBc.addKey(d).getKey() === key);
        } else {
            this._selectedDayIdx = null;
        }
        super.onAfterSave(isNew, preventTableViewRefresh);
    }
}

export default withPermissionContext(WorkingPatternsFormView);